import {
  ActionIcon,
  Box,
  Button,
  Center,
  Loader,
  Modal,
  Paper,
  ScrollArea,
  Stack,
  Text,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Phone, X as XIcon } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import classes from './page.module.css';

import { useQueriesWithGlobal } from '@data-access';
import { fieldsQueries } from '@fields/data-access';
import { FullMainHeight } from '@ui';

export function IlotInfosPage() {
  const { t } = useTranslation();
  const [contactModalOpened, contactModalActions] = useDisclosure(false);
  const navigate = useNavigate({ from: '/map-infos' });
  const search = useSearch({ from: '/_app/map-infos' });
  const ilotId = search.ilotId;
  const queries = useQueriesWithGlobal(fieldsQueries);

  const ilotInfos = useQuery({
    ...queries.ilotInfos(ilotId),
  });

  const contactName = useMemo(() => {
    const firstName = ilotInfos.data?.account?.first_name?.trim() ?? '';
    const lastName = ilotInfos.data?.account?.last_name?.trim() ?? '';
    const cleanParts = [firstName, lastName].filter((part) => part && part !== '-');
    if (!cleanParts.length) return '-';
    return cleanParts.join(' ').toUpperCase();
  }, [ilotInfos.data?.account?.first_name, ilotInfos.data?.account?.last_name]);

  const handleClose = () => {
    navigate({
      to: '/map',
      search: {
        ilotId: search.ilotId,
        pointId: search.pointId,
        pointCode: search.pointCode,
        sampleId: search.sampleId,
        lng: search.lng,
        lat: search.lat,
        zoom: search.zoom,
        scope: search.scope,
      },
    });
  };

  const handleOpenContactModal = () => {
    contactModalActions.open();
  };

  const handleContactFarmer = () => {
    const phone = ilotInfos.data?.account?.phone_number?.trim();
    if (!phone) return;
    window.location.href = `tel:${phone}`;
  };

  return (
    <FullMainHeight.Root>
      <FullMainHeight.Content className={classes.content}>
        <Stack gap={0} h="100%">
          <header className={classes.header}>
            <Text className={classes.headerTitle}>{t('sampling.ilotInfos.title')}</Text>
            <ActionIcon
              aria-label={t('sampling.ilotInfos.closeAria')}
              color="dark"
              onClick={handleClose}
              radius="xl"
              size={32}
              variant="light"
            >
              <XIcon size={18} />
            </ActionIcon>
          </header>

          <ScrollArea h="100%">
            <Stack className={classes.cardsStack} gap="md">
              <Paper className={classes.card} p="md" radius="lg" withBorder>
                <Text className={classes.sectionTitle}>
                  <span className={classes.sectionUnderline}>
                    {t('sampling.ilotInfos.farmerContact')}
                  </span>
                </Text>

                {ilotInfos.isLoading ? (
                  <Center py="md">
                    <Loader size="sm" />
                  </Center>
                ) : (
                  <Stack gap={4} mt="sm">
                    <Text className={classes.bodyText}>
                      {t('sampling.ilotInfos.name')} : {contactName}
                    </Text>
                    <Text className={classes.bodyText}>
                      {t('sampling.ilotInfos.farm')} : {ilotInfos.data?.name ?? '-'}
                    </Text>
                  </Stack>
                )}

                <Center mt="md">
                  <Button
                    className={classes.contactButton}
                    disabled={!ilotInfos.data?.account?.phone_number}
                    onClick={handleOpenContactModal}
                    radius="xl"
                  >
                    {t('sampling.ilotInfos.contactFarmer')}
                  </Button>
                </Center>
              </Paper>

              <Paper className={classes.card} p="md" radius="lg" withBorder>
                <Text className={classes.sectionTitle}>
                  <span className={classes.sectionUnderline}>
                    {t('sampling.ilotInfos.samplerInfo')}
                  </span>
                </Text>

                <Stack gap={10} mt="sm">
                  <Text className={classes.bodyText}>
                    {t('sampling.ilotInfos.programContext', {
                      organization: ilotInfos.data?.organization?.name ?? '-',
                    })}
                  </Text>
                  <Text className={classes.bodyText}>
                    {t('sampling.ilotInfos.precifieldFrame')}
                  </Text>
                  <Text className={classes.bodyText}>
                    {t('sampling.ilotInfos.questionGuidance')}
                  </Text>
                </Stack>
              </Paper>
            </Stack>
          </ScrollArea>
        </Stack>
      </FullMainHeight.Content>

      <Modal
        centered
        onClose={contactModalActions.close}
        opened={contactModalOpened}
        radius="lg"
        title={null}
        withCloseButton={false}
      >
        <Stack gap="sm">
          <Box className={classes.modalHeader}>
            <Text className={classes.sectionTitle}>
              <span className={classes.sectionUnderline}>
                {t('sampling.ilotInfos.contactModalTitle')}
              </span>
            </Text>
            <ActionIcon
              aria-label={t('sampling.ilotInfos.contactModalCloseAria')}
              color="dark"
              onClick={contactModalActions.close}
              radius="xl"
              size={32}
              variant="light"
            >
              <XIcon size={18} />
            </ActionIcon>
          </Box>
          <Text className={classes.bodyText}>
            {t('sampling.ilotInfos.contactExceptional')}
          </Text>
          <Text className={classes.bodyText}>
            {t('sampling.ilotInfos.contactReferPrecifield')}
          </Text>
          <Text className={classes.bodyText}>
            {t('sampling.ilotInfos.contactReminder', {
              organization: ilotInfos.data?.organization?.name ?? '-',
            })}
          </Text>

          <Button
            className={classes.callButton}
            disabled={!ilotInfos.data?.account?.phone_number}
            leftSection={<Phone size={16} />}
            onClick={handleContactFarmer}
            radius="xl"
          >
            {ilotInfos.data?.account?.phone_number ?? t('sampling.ilotInfos.phoneUnavailable')}
          </Button>
        </Stack>
      </Modal>
    </FullMainHeight.Root>
  );
}

export default IlotInfosPage;
